<template>
  <ConfirmationModal
    ref="moveSessionModal"
    modal
    :header="modalHeader"
    buttonTextConfirm="Move"
    buttonTextCancel="Cancel"
    isVibrantPurple
    smallerTitle
    class="move-session-modal"
    >
 
    <div v-focustrap>


    <div class="folders-list all-sessions">
      <p>All Sessions</p>
      <RadioButton
        v-model="moveSessionLocation"
        value="allSessions"
        aria-label="Move to All Sessions"
      >
      </RadioButton>
    </div>


    <Accordion>
      <AccordionPanel>
        <AccordionHeader>
          <div>
            <span class="accordion-header">
              My Folders 
            </span>
            <span class="accordion-length">
              ({{ allFolders.length }})
            </span>
          </div>
        </AccordionHeader>
        <div class="folder-items-container">
          <AccordionContent
            v-for="folder in allFolders"
            :key="folder.id">
            <div class="folders-list">
              <p>{{ folder.title }}</p>
              <RadioButton
                v-model="moveSessionLocation"
                :value="folder.id"
                :aria-label="`Move to ${folder.title}`"
              >
              
              </RadioButton>
            </div>
          </AccordionContent>
        </div>
      </AccordionPanel>
    </Accordion>


    <div class="create-folder">
        <template v-if="!showCreateFolderInput">
          <Button
            class="create-folder-button"
            label="Create new folder"
            variant="text"
            @click="showCreateFolderInput = true"
            
            @keydown="($event) => {
              if ($event.key === 'Enter') {
                $event.preventDefault();
                showCreateFolderInput = true;
              }
            }"
            aria-label="Create new folder"
            :aria-labelledby="showCreateFolderInput ? 'create-folder-input' : ''"  
          >
            <template #icon>
              <img
                role="presentation"
                :src="plusWhite"
                alt="Create new folder"
                class="create-folder-icon" />
            </template>
          </Button>
        </template>

        <template v-else>
          <div class="input-wrapper">
            <span class="p-input-icon-right">
                <InputText
                    id="newFolderName"
                    v-model="newFolderName"
                    class="input"
                    maxlength="50"
                    @keydown.enter="handleSaveFolder"
                />
                <img
                    aria-label="Clear input"
                    v-if="newFolderName"
                    @click="newFolderName = ''"
                    :src=CloseCircle
                    alt="Clear input"
                    class="clear-icon"
                />
            </span>
          </div>


          <Button
            label="Save"
            variant="text"
            @click="handleSaveFolder"
            @keydown="($event) => {
              if ($event.key === 'Enter') {
                $event.preventDefault();
                handleSaveFolder();
              }
            }"
            :disabled="!isNewFolderNameValid"
            :loading="createFolderLoading"
            aria-label="Save"
            class="create-folder-save" 
          />
          <Button
            autofocus
            label="Cancel"
            variant="text"
            @click="handleCancelFolder"
            @keydown="($event) => {
              if ($event.key === 'Enter') {
                $event.preventDefault();
                handleCancelFolder();
              }
            }"
            aria-label="Cancel"
            class="create-folder-cancel" 
          />
        </template>
      </div>
    </div>
  </ConfirmationModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import ConfirmationModal from './ConfirmationModal.vue';
import RadioButton from 'primevue/radiobutton';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import plusWhite from '@/assets/images/Plus-white.svg';
import { updateSession } from '../services/sessions';
import { Session } from '@/types/session-interfaces';
import CloseCircle from "@/assets/images/CloseCircle.svg";
import { useStore } from 'vuex';

const store = useStore();

const emit = defineEmits([
  'reloadPage'
]);

// Template refs
const moveSessionModal = ref<InstanceType<typeof ConfirmationModal> | null>(
  null
);

// State
const moveSessionLocation = ref<string | null>(null);
const showCreateFolderInput = ref(false);
const newFolderName = ref('');
const createFolderLoading = ref(false);

// Computed
const modalHeader = computed(() => {
  const count = selection.value.length;
  return `Move ${count} session${count === 1 ? '' : 's'} to`;
});

const isNewFolderNameValid = computed(
  () => newFolderName.value.trim().length > 0
);

// Methods
const resetCreateFolderState = () => {
  showCreateFolderInput.value = false;
  newFolderName.value = '';
};

const handleSaveFolder = async () => {
  if (!isNewFolderNameValid.value) return;

  createFolderLoading.value = true;
  try {
    const payload = {
        title: newFolderName.value.trim(),
        type: "folder",
    };
    const newFolder = await store.dispatch("itemList/createItem", payload)
    moveSessionLocation.value = newFolder.id;

    resetCreateFolderState();
  } catch (error) {
    console.error('Failed to create folder:', error);
    // TODO: Show user-friendly error message
  } finally {
    createFolderLoading.value = false;
  }
};

const handleCancelFolder = () => {
  resetCreateFolderState();
};

const selection = computed({
  get: () =>  store.getters['itemList/getSelectedItems'],
  set: (val) => store.dispatch('itemList/updateSelection', val),
});

const allFolders = computed(() =>  {
  return store.getters['itemList/getAllFolders']
});

const show = async () => {
  if (!moveSessionModal.value) {
    console.error('Modal reference is not available');
    return false;
  }

  const confirmed = await moveSessionModal.value.show();

  if (confirmed && moveSessionLocation.value) {
    const folderId =
      moveSessionLocation.value === 'allSessions'
        ? null
        : moveSessionLocation.value;
    try {
      await Promise.all(
        selection.value.map((item: Session) =>
          updateSession(item.id, { folderId })
        )
      );
      store.commit("itemList/clearSelection");
      emit('reloadPage');
      moveSessionLocation.value = null;
      return true;
    } catch (error) {
      console.error('Failed to move sessions:', error);
      // TODO: Show user-friendly error message
      return false;
    }
  }
  return false;
};

defineExpose({
  show,
});
</script>

<style lang="scss">
.move-session-modal {
  .folders-list {
    display: flex;
    justify-content: space-between;
    --p-radiobutton-icon-checked-color: #c77bff;
    p {
      color: map-get($colours, white);
    }
    &.all-sessions {
      margin-right: 0.3rem;
      padding: 1.125rem 0 1.125rem 0;
    }
  }
  
  .create-folder {
    display: flex;
    flex-direction: row;
    align-items: center;
    color: map-get($colours, white);
    padding: 1.125rem 0 1.125rem 0;
    
    .input-wrapper {
      width: 100%;
      position: relative;

      .p-inputtext {
        @include callout;
        background: map-get($colours, accent-purple-3);
        height: 4rem;
        min-width: 24.4rem;
        color: white;
        &:focus {
          background: map-get($colours, accent-purple-2);
        }
      }

      .clear-icon {
        position: absolute;
        top: 30%;
        right: 2.2rem;
        width: 1.6rem;
        height: 1.6rem;
        cursor: pointer;
        pointer-events: auto;
        z-index: 2;
        filter: invert(1);
      }
    }
    
    .create-folder-save .p-button-label {
      @include callout;
      color: white;
    }
    .create-folder-cancel .p-button-label {
      @include callout;
      color: map-get($colours, grey-3);
    }
    .create-folder-button {
      gap: 1rem;
      img {
        width: 1.4rem;
      }
    }
  }
  
  .p-accordion {
    --p-accordion-content-padding: 1.125rem 0 1.125rem 1.125rem;
    
    .folder-items-container {
      max-height: 18rem;
      overflow-y: auto;
    }

    .p-accordionheader {
      gap: 0.6rem;
      padding-right: 0.8rem;
      padding-left: 0;

      .accordion-header-wrapper {
        display: flex;
        align-items: center;
      }

      .accordion-header {
        @include section-sub-header;
        font-weight: bold;
      }
      
      .accordion-length {
        padding-bottom: 0.2rem;
        @include callout;
        color: map-get($colours, grey-2);
        line-height: 2.4rem;
      }
    }
  }
}
</style>